/**
 * Case Studies List — filter bar UI.
 *
 * Deliberately thin. The checkboxes are real form controls and HTMX owns the
 * fetching, swapping and history, so this only tracks which dropdown is open
 * and whether anything is selected (to show "Clear all"). Keeping selection
 * state out of here means there's one source of truth — the form — and no way
 * for the UI and the request to disagree.
 */
window.CaseStudiesList = () => {
	return {
		/** Key of the open desktop dropdown, or null. */
		open: null,

		/** Mobile panel. */
		panel: false,

		hasFilters: false,

		init() {
			this.syncHasFilters();
			this.measureTriggers();
			this.focusResultsOnPageChange();

			// CAPTURE phase, and this matters. Every term renders twice — once
			// in its desktop dropdown, once in the mobile tray — so a box and
			// its twin have to agree BEFORE htmx serialises the form. htmx
			// listens on the form during bubbling; this element is an ancestor,
			// so a bubbling listener here would run after htmx had already read
			// the values. Unchecking a box then still sent its twin's value and
			// the filter appeared not to clear. Capturing runs it first.
			this.$el.addEventListener(
				'change',
				(event) => {
					this.mirrorTwin(event.target);
					this.syncHasFilters();
				},
				true
			);

			window.addEventListener('resize', () => this.measureTriggers());
		},

		/**
		 * Publish each dropdown trigger's height as --cs-trigger-h.
		 *
		 * The open panel is a single box that starts above its trigger so the
		 * background wraps the label, which means its top padding has to clear
		 * a label whose height CSS can't know — it depends on the font and on
		 * text-box-trim. Measuring is the only honest way to get it.
		 */
		measureTriggers() {
			this.$el.querySelectorAll('.cs-group').forEach((group) => {
				const trigger = group.querySelector('button');
				if (trigger) group.style.setProperty('--cs-trigger-h', `${trigger.offsetHeight}px`);
			});
		},

		/**
		 * Move focus to the results, but only when the visitor changed PAGE.
		 *
		 * Filtering shouldn't yank focus off the checkbox someone is still
		 * working through — they may be about to tick a second one. Paging is
		 * different: the whole list has been replaced, so landing at the top of
		 * it is the helpful thing. Same reason the form's swap doesn't scroll
		 * and the pagination's does.
		 */
		focusResultsOnPageChange() {
			const results = document.getElementById('cs-results');
			if (!results) return;

			results.addEventListener('htmx:afterSwap', (event) => {
				this.announceCount(results);

				const trigger = event.detail?.requestConfig?.elt;
				if (trigger?.closest('nav[aria-label="Case studies pagination"]')) {
					results.focus({ preventScroll: true });
				}
			});
		},

		/**
		 * Speak how many results the change produced.
		 *
		 * The count comes from a data attribute the server renders with the
		 * results, not from counting cards here — the server already knows the
		 * total, and a page only ever holds six of it.
		 */
		announceCount(results) {
			const status = document.getElementById('cs-status');
			const marker = results.querySelector('[data-cs-count]');
			if (!status || !marker) return;

			const total = Number(marker.dataset.csCount);

			status.textContent =
				total === 1 ? '1 case study found.' : `${total} case studies found.`;
		},

		/**
		 * Keep the desktop and mobile copies of a term in step.
		 *
		 * Every term is rendered twice — once in its desktop dropdown, once in
		 * the mobile panel — because the two layouts group them differently
		 * and can't share one DOM node. Without this, ticking a box on desktop
		 * then opening the panel at a narrower width would show it unticked.
		 * Duplicate values in the submitted form are harmless: the query
		 * de-duplicates slugs.
		 */
		mirrorTwin(input) {
			if (!input || input.type !== 'checkbox') return;

			this.$el
				.querySelectorAll(`input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`)
				.forEach((twin) => {
					if (twin !== input) twin.checked = input.checked;
				});
		},

		syncHasFilters() {
			this.hasFilters = this.$el.querySelectorAll('input[type="checkbox"]:checked').length > 0;
		},

		toggle(key) {
			this.open = this.open === key ? null : key;
		},

		closeAll() {
			this.open = null;
			this.panel = false;
		},

		/**
		 * Escape closes whatever is open AND hands focus back to the control
		 * that opened it — otherwise focus is left on a panel that no longer
		 * exists and the next Tab restarts from the top of the document.
		 *
		 * Separate from closeAll() on purpose: click-outside also closes, and
		 * pulling focus back to the trigger there would yank it away from
		 * whatever the visitor just deliberately clicked.
		 */
		closeOnEscape() {
			const openGroup = this.open;
			const panelWasOpen = this.panel;

			if (!openGroup && !panelWasOpen) return;

			this.closeAll();

			const selector = openGroup
				? `[aria-controls="cs-group-${openGroup}"]`
				: '[aria-controls="cs-filter-panel"]';

			this.$el.querySelector(selector)?.focus();
		},

		/**
		 * First control that's actually on screen at this breakpoint.
		 *
		 * The bar renders a desktop set and a mobile set and hides one with
		 * display:none; calling focus() on a hidden element silently does
		 * nothing, so a fixed selector would work at one width and drop focus
		 * at the other.
		 */
		firstVisibleControl() {
			const candidates = this.$el.querySelectorAll(
				'button[aria-controls="cs-filter-panel"], .cs-group > button'
			);

			return [...candidates].find((el) => el.offsetParent !== null) || null;
		},

		/**
		 * Uncheck everything and re-run the query.
		 *
		 * `form.reset()` restores the checkboxes to their SERVER-RENDERED
		 * state, which on a deep link like ?industry=dental-medical is "still
		 * checked" — the opposite of clearing. So each box is unchecked
		 * explicitly, then one synthetic change event fires the single HTMX
		 * request (setting .checked in script doesn't emit one).
		 */
		clearAll(trigger) {
			const form = trigger.closest('form');
			if (!form) return;

			form.querySelectorAll('input[type="checkbox"]:checked').forEach((box) => {
				box.checked = false;
			});

			this.syncHasFilters();
			this.closeAll();

			form.dispatchEvent(new Event('change', { bubbles: true }));

			// This button disappears with the last filter (x-show), and focus on
			// a display:none element falls back to <body> — so the next Tab
			// would restart from the top of the page. Park it somewhere stable.
			this.firstVisibleControl()?.focus();
		},
	};
};
